import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Plus, RefreshCcw, ChevronRight, FileText, Save, X,
  Trash2, PenLine, BadgeCheck, BadgeX, Layers, CheckCircle2, Archive,
  ArrowDownCircle, ArrowUpCircle, MinusCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Tipos de Comprobante - Enterprise
 * Refactorizado al estilo Productos / Condiciones Pago
 * Estructura: KPIs + Formulario Inline (Izq) + Listado (Der)
 */
function TiposComprobante() {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // form states
  // const [formOpen, setFormOpen] = useState(false); // COMENTADO: Formulario siempre visible en layout dividido
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [action, setAction] = useState('subtract'); // 'subtract', 'add', 'neutral'
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile?.organization_id) fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  async function fetchItems() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('invoice_types')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('name', { ascending: true });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar comprobantes');
    } finally {
      setLoading(false);
    }
  }

  async function guardar(e) {
    e.preventDefault();
    if (!name.trim()) return toast.error('El nombre es obligatorio');

    setSaving(true);
    try {
      const payload = {
        name: name.trim().toUpperCase(),
        code: code.trim().toUpperCase(),
        action,
        is_active: isActive,
        organization_id: profile.organization_id
      };

      if (editingId) {
        const { error } = await supabase
          .from('invoice_types')
          .update(payload)
          .eq('id', editingId);
        if (error) throw error;
        toast.success('Comprobante actualizado');
      } else {
        const { error } = await supabase
          .from('invoice_types')
          .insert([payload]);
        if (error) throw error;
        toast.success('Comprobante creado');
      }
      
      limpiarForm();
      fetchItems();
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este tipo de comprobante?')) return;
    try {
      const { error } = await supabase
        .from('invoice_types')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Eliminado correctamente');
      fetchItems();
    } catch (error) {
      console.error(error);
      toast.error('No se puede eliminar (en uso)');
    }
  }

  function editar(item) {
    setEditingId(item.id);
    setName(item.name);
    setCode(item.code || '');
    setAction(item.action || 'subtract');
    setIsActive(item.is_active);
    // setFormOpen(true); // Formulario siempre visible
  }

  function limpiarForm() {
    setEditingId(null);
    setName('');
    setCode('');
    setAction('subtract');
    setIsActive(true);
    // setFormOpen(false);
  }

  // --- KPIS ---
  const kpis = useMemo(() => {
    const total = items.length;
    const activos = items.filter(i => i.is_active).length;
    const restanStock = items.filter(i => i.action === 'subtract').length;
    return { total, activos, restanStock };
  }, [items]);

  return (
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <FileText size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Tipos de Comprobante</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Documentos comerciales y su impacto en stock.
            </p>
          </div>
        </div>
      </div>

      {/* KPI SECTION */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Tipos</p>
            <Layers size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Definidos</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Activos</p>
            <CheckCircle2 size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.activos}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Disponibles en venta</p>
        </div>

        <div className="imp-kpi">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Restan Stock</p>
                <ArrowDownCircle size={16} className="text-rose-500" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.restanStock}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Movimiento de salida</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* COLUMNA IZQUIERDA: FORMULARIO */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Plus size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                  {editingId ? 'Editar Comprobante' : 'Nuevo Comprobante'}
                </span>
              </div>
              {editingId && (
                <button onClick={limpiarForm} className="text-[9px] font-bold uppercase text-white/60 hover:text-white flex items-center gap-1 transition-colors">
                    <X size={12}/> Cancelar
                </button>
              )}
            </div>

            <div className="p-4">
                <form onSubmit={guardar} className="space-y-3">
                    <div className="space-y-1">
                        <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Nombre (Ej: Remito)</label>
                        <input 
                            type="text"
                            placeholder="EJ: FACTURA A, REMITO X..."
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                            autoFocus
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Código / Letra</label>
                            <input 
                                type="text"
                                placeholder="EJ: A, B, X..."
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Impacto Stock</label>
                            <select
                                value={action}
                                onChange={(e) => setAction(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner text-slate-700 cursor-pointer"
                            >
                                <option value="subtract">Resta (Salida)</option>
                                <option value="add">Suma (Devolución)</option>
                                <option value="neutral">Neutro (Presupuesto)</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 pt-2 pb-1">
                        <input 
                            type="checkbox" 
                            id="isActiveCheck"
                            checked={isActive} 
                            onChange={(e) => setIsActive(e.target.checked)}
                            className="w-4 h-4 rounded border-slate-300 text-[#51A7FD] focus:ring-[#51A7FD] cursor-pointer"
                        />
                        <label htmlFor="isActiveCheck" className="text-[10px] font-bold text-slate-600 uppercase cursor-pointer select-none">
                            Habilitado para uso
                        </label>
                    </div>

                    <div className="pt-2">
                        <button 
                            type="submit" 
                            disabled={saving}
                            className={`w-full py-3.5 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 shadow-lg
                                ${editingId 
                                    ? 'bg-[#51A7FD] text-white hover:bg-blue-600' 
                                    : 'bg-[#3ff2aa] text-[#0B2A36] hover:scale-[1.02] active:scale-95 shadow-emerald-500/20'}`}
                        >
                            {saving ? <RefreshCcw className="animate-spin" size={14} /> : <Save size={16} />}
                            {editingId ? 'Actualizar' : 'Guardar Comprobante'}
                        </button>
                    </div>
                </form>
            </div>
          </div>
        </div>

        {/* COLUMNA DERECHA: LISTADO */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[400px]">
            <div className="overflow-x-auto imp-table-wrap">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    <th className="px-5 py-3 pl-5">Comprobante</th>
                    <th className="px-4 py-3 text-center">Código</th>
                    <th className="px-4 py-3 text-center">Impacto Stock</th>
                    <th className="px-4 py-3 text-right">Estado</th>
                    <th className="px-4 py-3 text-right pr-5">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="py-12 text-center text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                        <RefreshCcw className="animate-spin mx-auto mb-2" size={20} />
                        Cargando...
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="py-16 text-center opacity-50 flex flex-col items-center">
                        <FileText size={40} className="text-slate-300 mb-2" strokeWidth={1} />
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          No hay comprobantes definidos
                        </p>
                      </td>
                    </tr>
                  ) : (
                    items.map((t) => (
                      <tr key={t.id} className="hover:bg-blue-50/20 transition-all group">
                        <td className="px-5 py-3 pl-5">
                          <span className={`text-[11px] font-black uppercase tracking-tight ${t.is_active ? 'text-slate-700' : 'text-slate-400 line-through'}`}>{t.name}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                            {t.code || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                            {t.action === 'subtract' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-50 border border-rose-100 text-rose-600 text-[9px] font-black uppercase">
                                    <ArrowDownCircle size={10} /> Resta
                                </span>
                            )}
                            {t.action === 'add' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-100 text-emerald-600 text-[9px] font-black uppercase">
                                    <ArrowUpCircle size={10} /> Suma
                                </span>
                            )}
                            {t.action === 'neutral' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-50 border border-slate-100 text-slate-500 text-[9px] font-black uppercase">
                                    <MinusCircle size={10} /> Neutro
                                </span>
                            )}
                        </td>
                        <td className="px-4 py-3 text-right">
                            {t.is_active ? (
                                <span className="text-[9px] font-bold text-emerald-500 uppercase">Activo</span>
                            ) : (
                                <span className="text-[9px] font-bold text-slate-400 uppercase">Inactivo</span>
                            )}
                        </td>
                        <td className="px-4 py-3 pr-5 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => editar(t)}
                              className="p-1.5 text-slate-300 hover:text-[#51A7FD] hover:bg-blue-50 rounded-lg transition-all"
                              title="Editar"
                            >
                              <PenLine size={14} />
                            </button>
                            <button
                              onClick={() => eliminar(t.id)}
                              className="p-1.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
}

export default TiposComprobante;